import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Clock,
  CurrencyCircleDollar,
  Lightbulb,
  Receipt,
  ShieldCheck,
  Warning,
} from '@phosphor-icons/react';

import { useRevenueLeaks } from '@/features/revenue';
import type { RevenueLeak } from '@/features/revenue';
import { useCurrentBranch } from '@/shared/context/TenantContext';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { formatDateTime, formatMoney } from '@/shared/utils/formatters';

export function RevenueLeakPage(): JSX.Element {
  const branch = useCurrentBranch();
  const { data: leaks, isLoading } = useRevenueLeaks(branch?.branchId);
  const leakItems = leaks ?? [];
  const totalMinor = leakItems.reduce((sum, leak) => sum + leak.amountMinor, 0);
  const oldestLeak = getOldestLeak(leakItems);
  const hasLeaks = leakItems.length > 0;

  return (
    <div className="px-biz-card-lg py-biz-content-lg max-w-[1400px] mx-auto flex flex-col gap-biz-content-lg">
      <header className={`rounded-biz-card border p-biz-card-lg flex items-start sm:items-center justify-between gap-biz-content-lg flex-wrap ${
        hasLeaks
          ? 'border-biz-warning-border bg-gradient-to-br from-biz-warning-subtle to-biz-surface'
          : 'border-biz-success-border bg-gradient-to-br from-biz-success-subtle to-biz-surface'
      }`}>
        <div className="flex items-start gap-biz-content-lg min-w-0">
          <div className={`h-12 w-12 rounded-biz-btn flex items-center justify-center shrink-0 ${
            hasLeaks
              ? 'bg-biz-warning text-biz-warning-foreground'
              : 'bg-biz-success text-biz-success-foreground'
          }`}>
            {hasLeaks ? (
              <Warning size={24} weight="fill" aria-hidden="true" />
            ) : (
              <ShieldCheck size={24} weight="fill" aria-hidden="true" />
            )}
          </div>
          <div className="min-w-0">
            <h1 className="text-biz-2xl font-bold biz-heading">Revenue Leak</h1>
            <p className="text-biz-sm text-biz-muted-foreground mt-1">
              Tamamlanmış fakat tahsilatı kapanmamış randevular.
            </p>
            <p className={`text-biz-xs font-medium mt-2 ${hasLeaks ? 'text-biz-warning' : 'text-biz-success'}`}>
              {hasLeaks ? 'Aksiyon gerekli: açık tahsilatlar var.' : 'Temiz: açık tahsilat görünmüyor.'}
            </p>
          </div>
        </div>
        <Button asChild leftIcon={<ArrowRight size={18} />}>
          <Link to="/cashier">Kasaya git</Link>
        </Button>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-biz-content-lg">
        <Metric
          icon={<Receipt size={18} weight="fill" />}
          tone={hasLeaks ? 'warning' : 'success'}
          label="Açık kayıt"
          value={`${leakItems.length}`}
        />
        <Metric
          icon={<CurrencyCircleDollar size={18} weight="fill" />}
          tone={totalMinor > 200000 ? 'danger' : hasLeaks ? 'warning' : 'success'}
          label="Riskli tutar"
          value={formatMoney(totalMinor / 100, oldestLeak?.currency ?? 'TRY')}
          emphasis
        />
        <Metric
          icon={hasLeaks ? <Warning size={18} weight="fill" /> : <ShieldCheck size={18} weight="fill" />}
          tone={hasLeaks ? 'warning' : 'success'}
          label="Durum"
          value={hasLeaks ? 'Aksiyon gerekli' : 'Temiz'}
        />
        <Metric
          icon={<Clock size={18} weight="fill" />}
          tone={oldestLeak ? 'warning' : 'neutral'}
          label="En eski açık"
          value={oldestLeak ? formatDateTime(oldestLeak.completedAt) : 'Yok'}
        />
      </section>

      <section className={hasLeaks ? 'grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-biz-content-lg' : ''}>
        <Card className="overflow-hidden">
          <CardHeader>
            <div>
              <h2 className="font-semibold biz-heading">Tahsilat açıkları</h2>
              <p className="text-biz-sm text-biz-muted-foreground">
                Backend `biz_bookings.status=completed` ve `payment_status in unpaid/partial` filtresiyle üretir.
              </p>
            </div>
          </CardHeader>
          <div className="divide-y divide-biz-border">
            {isLoading ? <LeakSkeleton /> : null}
            {!isLoading
              ? leakItems.map((leak) => (
                  <div key={leak.bookingId} className="px-biz-card py-biz-content-lg flex items-center justify-between gap-biz-content-lg">
                    <div className="flex items-center gap-biz-content-lg min-w-0">
                      <div className="w-11 h-11 rounded-biz-card bg-biz-warning-subtle text-biz-warning flex items-center justify-center shrink-0">
                        <Warning size={20} weight="fill" aria-hidden="true" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{leak.customerName}</p>
                        <p className="text-biz-sm text-biz-muted-foreground">
                          {leak.serviceName} · {formatDateTime(leak.completedAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-biz-content shrink-0">
                      <StatusBadge variant="warning" label="Ödenmedi" />
                      <span className="font-semibold biz-numeric">
                        {formatMoney(leak.amountMinor / 100, leak.currency)}
                      </span>
                    </div>
                  </div>
                ))
              : null}
            {!isLoading && leakItems.length === 0 ? (
              <div className="px-biz-card py-biz-card">
                <div className="rounded-biz-card border border-biz-success-border bg-biz-success-subtle px-biz-content-lg py-biz-content-lg">
                  <div className="flex items-start gap-biz-content">
                    <ShieldCheck size={22} weight="fill" className="text-biz-success shrink-0" />
                    <div>
                      <p className="font-semibold text-biz-success">Tahsilat açığı bulunamadı.</p>
                      <p className="text-biz-sm text-biz-muted-foreground mt-1">
                        Tamamlanmış randevular için açık ödeme görünmüyor.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </Card>

        {hasLeaks ? <CollectionInsightCard oldestLeak={oldestLeak} totalMinor={totalMinor} /> : null}
      </section>
    </div>
  );
}

function getOldestLeak(leaks: RevenueLeak[]): RevenueLeak | null {
  return leaks.reduce<RevenueLeak | null>((oldest, leak) => {
    if (!oldest) return leak;
    return new Date(leak.completedAt).getTime() < new Date(oldest.completedAt).getTime()
      ? leak
      : oldest;
  }, null);
}

function Metric({
  icon,
  tone,
  label,
  value,
  emphasis,
}: {
  icon: JSX.Element;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
  label: string;
  value: string;
  emphasis?: boolean;
}): JSX.Element {
  const toneClass = {
    success: 'bg-biz-success-subtle text-biz-success border-biz-success-border',
    warning: 'bg-biz-warning-subtle text-biz-warning border-biz-warning-border',
    danger: 'bg-biz-danger-subtle text-biz-danger border-biz-danger-border',
    neutral: 'bg-biz-muted text-biz-muted-foreground border-biz-border',
  }[tone];

  return (
    <Card>
      <CardBody className="p-biz-content">
        <div className="flex items-center gap-2 text-biz-xs font-semibold uppercase tracking-wide text-biz-muted-foreground">
          <span className={`h-8 w-8 rounded-full border flex items-center justify-center ${toneClass}`}>
            {icon}
          </span>
          {label}
        </div>
        <p className={`${emphasis ? 'text-biz-2xl' : 'text-biz-xl'} font-bold biz-heading mt-biz-content biz-numeric`}>
          {value}
        </p>
      </CardBody>
    </Card>
  );
}

function CollectionInsightCard({
  oldestLeak,
  totalMinor,
}: {
  oldestLeak: RevenueLeak | null;
  totalMinor: number;
}): JSX.Element {
  return (
    <Card className="h-fit">
      <CardHeader>
        <div className="flex items-start gap-biz-content">
          <div className="h-10 w-10 rounded-biz-card bg-biz-warning-subtle text-biz-warning flex items-center justify-center shrink-0">
            <Lightbulb size={20} weight="fill" aria-hidden="true" />
          </div>
          <div>
            <h2 className="font-semibold biz-heading">AI tahsilat önerisi</h2>
            <p className="text-biz-sm text-biz-muted-foreground">
              En eski açık kaydı önce kapatın.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardBody className="pt-0 text-biz-sm text-biz-muted-foreground">
        <p>
          Bugün kapanmadan {formatMoney(totalMinor / 100, oldestLeak?.currency ?? 'TRY')} tahsilat
          riskini kasaya taşıyın.
        </p>
        {oldestLeak ? (
          <p className="mt-biz-content">
            Öncelik: <span className="font-semibold text-biz-foreground">{oldestLeak.customerName}</span>
            {' '}· {formatDateTime(oldestLeak.completedAt)}
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function LeakSkeleton(): JSX.Element {
  return (
    <>
      {[0, 1, 2].map((item) => (
        <div key={item} className="px-biz-card py-biz-content-lg flex items-center justify-between gap-biz-content-lg">
          <div className="flex items-center gap-biz-content-lg flex-1">
            <div className="h-11 w-11 rounded-biz-card bg-biz-muted animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-4 w-48 rounded-full bg-biz-muted animate-pulse" />
              <div className="h-3 w-72 max-w-full rounded-full bg-biz-muted animate-pulse" />
            </div>
          </div>
          <div className="h-7 w-24 rounded-full bg-biz-muted animate-pulse" />
        </div>
      ))}
    </>
  );
}
